import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Pagination } from "antd";
import moment from "moment";

import { ProfileViewerAction, SidebarCountAction } from "../../store/actions";

import useScreenSize from "../../utils/usescreensize";

import ProfileTab from "../../components/tab/profiletab";
import DashboardCard from "../../components/dashboardcard/dashboardcard";
import { WhitePurpleButton } from "../../components/button";
import SortDropdown from "../../components/dropdown/sortdropdown";
import Cardloader from "../../components/loader/cardloader";
import Spacer from "../../components/spacer/spacer";
import UnderHeaderMenu from "../../components/underheadermenu";

import images from "../../assets";

import "./styles.css";

const ProfileViewers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const screenSize = useScreenSize();
  const [searchParams, setSearchParams] = useSearchParams();

  const validTabs = ["My Profile Viewers", "Profile Viewed by Me"];
  const defaultTab = validTabs.includes(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "My Profile Viewers";
  const defaultPage = parseInt(searchParams.get("page")) || 1;

  const { profileViewed, isLoading, profileViewer } = useSelector(
    (state) => state.profileView
  );

  const [state, setState] = useState({
    activeTab: defaultTab,
    sortOption: "desc",
  });
  const [pageNo, setPageNo] = useState(defaultPage);
  const [pageSize] = useState(8);

  useEffect(() => {
    dispatch(SidebarCountAction.updateSidebarCount({ table: "profileview" }));
  }, []);

  useEffect(() => {
    fetchProfileData();
  }, [state.activeTab, state.sortOption]);

  useEffect(() => {
    setSearchParams({ tab: state.activeTab, page: pageNo.toString() });
  }, [state.activeTab, pageNo]);

  useEffect(() => {
    const tabFromURL = searchParams.get("tab") || "My Profile Viewers";
    const pageFromURL = parseInt(searchParams.get("page")) || 1;

    if (tabFromURL !== state.activeTab) {
      setState((prev) => ({ ...prev, activeTab: tabFromURL }));
    }

    if (pageFromURL !== pageNo) {
      setPageNo(pageFromURL);
    }
  }, [searchParams]);

  const fetchProfileData = () => {
    const payload = {
      filter: state?.activeTab,
      sort: state?.sortOption,
    };

    if (state?.activeTab === "My Profile Viewers") {
      dispatch(ProfileViewerAction.getProfileViewer(payload));
    } else {
      dispatch(ProfileViewerAction.getProfileViewed(payload));
    }
  };

  const tabItems = [
    { name: "My Profile Viewers", title: "My Profile Viewers" },
    { name: "Profile Viewed by Me", title: "Profile Viewed by Me" },
  ];

  const activeData =
    state?.activeTab === "My Profile Viewers" ? profileViewer : profileViewed;

  const sortedData = useMemo(() => {
    if (!activeData) return [];
    let sortedData = [...activeData];

    if (state.sortOption === "desc") {
      sortedData.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    } else if (state.sortOption === "asc") {
      sortedData.sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt));
    }

    return sortedData;
  }, [activeData, state.sortOption]);

  const paginatedData = useMemo(() => {
    return sortedData.slice((pageNo - 1) * pageSize, pageNo * pageSize);
  }, [sortedData, pageNo, pageSize]);

  return (
    <div>
      {screenSize.width < 1023 && (
        <UnderHeaderMenu titlename="Profile Viewers">
          <div style={{ width: "110px" }}>
            <SortDropdown
              data={[
                { label: "Newest First", value: "desc" },
                { label: "Oldest First", value: "asc" },
              ]}
              onChange={(e) => setState({ ...state, sortOption: e })}
              value={state?.sortOption || undefined}
              placeholder="Sort by"
            />
          </div>
        </UnderHeaderMenu>
      )}
      <Spacer height="16px" />
      <div className="profileviewers-section-top-part-container">
        <div></div>
        <div className="profileviewers-section-header-container">
          {screenSize.width > 1023 && (
            <h1 className="profileviewers-header">Profile Viewers</h1>
          )}
          <ProfileTab
            tabItems={tabItems}
            handleTab={(name) => {
              setState((prev) => ({ ...prev, activeTab: name }));
              setPageNo(1);
            }}
            activeTab={state?.activeTab}
            style={{
              background: "white",
              border: "1px solid #EAE6ED",
              width: "300px",
            }}
          />
        </div>
        {screenSize.width > 1023 && (
          <div className="profileviewers-sort-container">
            <div style={{ width: "150px" }}>
              <SortDropdown
                data={[
                  { label: "Newest First", value: "desc" },
                  { label: "Oldest First", value: "asc" },
                ]}
                onChange={(e) => setState({ ...state, sortOption: e })}
                value={state?.sortOption || undefined}
                placeholder="Sort by"
              />
            </div>
          </div>
        )}
      </div>
      <Spacer height="12px" />
      <div className="profileviewers-results-container">
        {isLoading ? (
          <Cardloader />
        ) : paginatedData.length > 0 ? (
          <>
            <div className="profileviewers-results">
              {paginatedData.map((item, index) => (
                <DashboardCard
                  item={item}
                  index={index}
                  key={index}
                  redirectedfrom={
                    state?.activeTab === "My Profile Viewers"
                      ? "profileViewer"
                      : "profileViewed"
                  }
                >
                  <div className="flex flex-col w-full gap-3">
                    <div className="profileviewers-card-text">
                      <p>Viewed on:</p>
                      <p>{moment(item?.updatedAt).fromNow()}</p>
                    </div>

                    <WhitePurpleButton
                      text="View Profile"
                      onClick={() =>
                        navigate(`/view-profile/${item?.username}`)
                      }
                    />
                  </div>
                </DashboardCard>
              ))}
            </div>
            <Spacer height="36px" />
            <div className="flex justify-center items-center">
              <Pagination
                pageSize={pageSize}
                total={sortedData.length}
                current={pageNo}
                showSizeChanger={false}
                onChange={(page) => setPageNo(page)}
              />
            </div>
            <Spacer height="24px" />
          </>
        ) : (
          <div className="flex flex-col justify-center items-center p-10 w-full gap-4">
            <img
              src={images.noActivityIcon}
              alt="no notifications"
              className="h-[160px] lg:h-full"
            />
            <div className="text-basePrimary font-bold text-[24px] lg:text-[32px]">
              {state?.activeTab === "My Profile Viewers"
                ? "No One Viewed You Yet"
                : "You Haven’t Viewed Anyone Yet"}
            </div>
            <p className="text-[14px] text-center max-w-[500px]">
              {state?.activeTab === "My Profile Viewers"
                ? "Once someone checks out your profile, you’ll see them here."
                : "The profiles you visit will show up here. Start exploring to find your match."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileViewers;
